import React, { useEffect, useState } from "react";
import serviceApi from "../../../services/serviceApi";
import environment from "../../../environments/environment";

type Assignment = {
    id: string;
    title: string;
    type: string;
    subject: string;
    description: string;
    closing_date: string;
};

type LearningItem = {
    id: string;
    theme: string;
    word: string;
    meaning: string;
};

type TimetableItem = {
    period: string;
    time: string;
    subject: string;
    incharge: string;
};

type ClassroomData = {
    activeExams: any[];
    exams: any[];
    dairy: any[];
    assignments: Assignment[];
    learningItems: LearningItem[];
    timetable: Record<string, TimetableItem[]>;
};

const Classroom: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [data, setData] = useState<ClassroomData | null>(null);
    const [showTests, setShowTests] = useState(false);
    const [showExams, setShowExams] = useState(false);
    const [showDiary, setShowDiary] = useState(false);
    const [showAssignments, setShowAssignments] = useState(true);
    const [showLearning, setShowLearning] = useState(true);
    const [showTimetable, setShowTimetable] = useState(true);

    useEffect(() => {
        Classroom(selectedDate);
    }, [selectedDate]);

    const formatDate = (date: Date) => {
        const dd = String(date.getDate()).padStart(2, "0");
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const yyyy = date.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    const Classroom = async (date: Date) => {
        setIsLoading(true);
        try {
            const res = await serviceApi.get(
                `${environment.classroom}?date=${formatDate(date)}`
            );
            if (res?.status === "SUCCESS") {
                setData(res?.dataList?.[0] || null);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const renderSectionHeader = (
        title: string,
        icon: string,
        color: string,
        expanded: boolean,
        onClick: () => void
    ) => (
        <div
            className="popup-header"
            style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: color,
                fontWeight: 600
            }}
        >
            <h3 style={{ color }}>
                <i
                    className={`fa ${icon}`}
                    style={{
                        marginRight: "8px",
                        color
                    }}
                />
                {title}
            </h3>
            <i
                className={`fa ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`}
                style={{ color: "#777" }}
                onClick={onClick}
            />
        </div>
    );

    const getTimetable = () => {
        if (!data?.timetable) return [];
        const key = Object.keys(data.timetable)[0];
        return key ? data.timetable[key] : [];
    };
    const startTest = (test: any) => {
        if (test?.testUrl) {
            window.open(test.testUrl, "_blank");
        }
    };

    const formatDuration = (minutes: number) => {
        if (!minutes) return "-";
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hrs && mins) return `${hrs}h ${mins}m`;
        if (hrs) return `${hrs}h`;
        return `${mins}m`;
    };

    return (
        <div className="notification-page">
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-overlay__content">
                        <div className="spinner spinner--lg" />
                        <span className="loading-text">Loading classroom...</span>
                    </div>
                </div>
            )}
            <h2 className="title-text notification-heading">Classroom</h2>
            <div className="date-picker-container">
                <button className="date-nav-btn" onClick={() => setSelectedDate(new Date(selectedDate.setDate(selectedDate.getDate() - 1)))}>
                    <i className="fa fa-chevron-left" />
                </button>
                <input
                    className="date-input"
                    type="date"
                    value={selectedDate.toISOString().split("T")[0]}
                    onChange={(e) => setSelectedDate(new Date(e.target.value))}
                />
                <button className="date-nav-btn" onClick={() => setSelectedDate(new Date(selectedDate.setDate(selectedDate.getDate() + 1)))}>
                    <i className="fa fa-chevron-right" />
                </button>
            </div>
            <div className="event-container">
                {renderSectionHeader(
                    "Today's Online Tests",
                    "fa-laptop",
                    "#30A6CA",
                    showTests,
                    () => setShowTests(!showTests)
                )}
                {showTests && (
                    <div className="events-list">
                        {data?.activeExams?.length ? (
                            data.activeExams.map((test: any) => (
                                <div key={test.id} >
                                    <div style={{ display: "inline-block", background: "#f3f3f3", padding: "2px 8px", borderRadius: "12px", fontWeight: 600, marginBottom: "6px", color: 'green' }}>
                                        Active
                                    </div>
                                    <div className="title-text">
                                        {test.title}
                                    </div>
                                    <div className="subtitle-text">
                                        {test.description || test.subject}
                                    </div>
                                    <div className="event-description">
                                        <b>Duration :</b> {formatDuration(test.duration)}
                                    </div>
                                    <div className="event-date" style={{ display: "flex", justifyContent: "space-between", color: "#dc3545", }}>
                                        <span>
                                            <i className="fa-regular fa-clock"></i>{" "}
                                            Ends : {test.endDate} - {test.endTime}
                                        </span>
                                        <button className="go-button" onClick={() => startTest(test)}>
                                            Start
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-events">
                                No active tests available.
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="event-container">
                {renderSectionHeader(
                    "Today's Exams",
                    "fa fa-pencil",
                    "#FFA739",
                    showExams,
                    () => setShowExams(!showExams)
                )}
                {showExams && (
                    <div className="events-list">
                        {data?.exams?.length ? (
                            data.exams.map((e: any) => (
                                <div key={e.id} className="event-item">
                                    <i className="fa fa-file-text" style={{ color: "#FFA739", marginRight: 6 }} />
                                    <div className="title-text">{e.title}</div>
                                </div>
                            ))
                        ) : (
                            <div className="no-events">No exams available</div>
                        )}
                    </div>
                )}
            </div>
            <div className="event-container">
                {renderSectionHeader(
                    "Today's Diary",
                    "fa-book",
                    "#8bc3c5",
                    showDiary,
                    () => setShowDiary(!showDiary)
                )}
                {showDiary && (
                    <div className="events-list">
                        {data?.dairy?.length ? (
                            data.dairy.map((d: any) => (
                                <div key={d.id} className="event-item">
                                    <i className="fa fa-pencil" style={{ color: "#8bc3c5", marginRight: 6 }} />
                                    <div className="title-text">{d.title}</div>
                                </div>
                            ))
                        ) : (
                            <div className="no-events">No diary entries</div>
                        )}
                    </div>
                )}
            </div>
            <div className="event-container">
                {renderSectionHeader(
                    "Today's Assignments",
                    "fa-tasks",
                    "#28a745",
                    showAssignments,
                    () => setShowAssignments(!showAssignments)
                )}
                {showAssignments && (
                    <div className="events-list">
                        {data?.assignments?.length ? (
                            data.assignments.map((a) => (
                                <div key={a.id} className="event-item" style={{ position: 'relative' }}>
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "8px",
                                            right: "10px",
                                            background: "var(--colorAccent)",
                                            color: "#fff",
                                            fontSize: "var(--sz-12)",
                                            padding: "2px 8px",
                                            borderRadius: "var(--radius-8)",
                                            fontWeight: 600
                                        }}
                                    >
                                        {a.type}
                                    </div>
                                    <div className="title-text">{a.title}</div>
                                    <div className="subtitle-text">{a.subject}</div>
                                    <div className="event-description">{a.description}</div>
                                </div>
                            ))
                        ) : (
                            <div className="no-events">No assignments available</div>
                        )}
                    </div>
                )}
            </div>
            <div className="event-container">
                {renderSectionHeader(
                    "Today's Learning Item",
                    "fa fa-lightbulb",
                    "#ff9800",
                    showLearning,
                    () => setShowLearning(!showLearning)
                )}
                {showLearning && (
                    <div className="events-list">
                        {data?.learningItems?.length ? (
                            <div className="event-item">
                                <div className="title-text">{data.learningItems[0].word}</div>
                                <div className="subtitle-text">{data.learningItems[0].theme}</div>
                                <div className="event-description">{data.learningItems[0].meaning}</div>
                            </div>
                        ) : (
                            <div className="no-events">No learning item today</div>
                        )}
                    </div>
                )}
            </div>
            <div className="event-container">
                {renderSectionHeader(
                    "Today's Time Table",
                    "fa-calendar",
                    "#30A6CA",
                    showTimetable,
                    () => setShowTimetable(!showTimetable)
                )}
                {showTimetable && (
                    <div className="events-list">
                        {getTimetable().length ? (
                            getTimetable().map((item, i) => (
                                <div key={i} className="event-item">
                                    <i className="fa fa-clock-o" style={{ color: "#30A6CA", marginRight: 6 }} />
                                    <div className="button-container">
                                        <div className="subtitle-text">{item.time}</div>
                                        <div className="title-text">{item.subject}</div>
                                    </div>
                                    <div className="event-description">{item.incharge}</div>
                                    <div className="event-date">Period {item.period}</div>
                                </div>
                            ))
                        ) : (
                            <div className="no-events">No timetable records found</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Classroom;